// #region Imports
import React, { useState, useEffect, useRef } from "react"
import ReactCrop, {
  centerCrop,
  makeAspectCrop,
  Crop,
  PixelCrop,
  convertToPixelCrop,
} from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css';

import {
  Badge,
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  InputGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Row,
  Table,
  UncontrolledAlert,
  UncontrolledTooltip,
} from "reactstrap"

import { canvasPreview } from './canvasPreview'
import { useDebounceEffect } from './useDebounceEffect'

import Slider from "rc-slider";
import 'rc-slider/assets/index.css';
// #endregion

// #region Global functions
// This is to demonstate how to make and center a % aspect crop
// which is a bit trickier so we use some helper functions.
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight,
    ),
    mediaWidth,
    mediaHeight,
  );
}

function centerAspectCrop256(mediaWidth, mediaHeight, aspect) {
  const size = Math.min(mediaWidth, mediaHeight, 256); // Asegurando que el recorte no sea mayor que la imagen o 256px
  return centerCrop(
    {
      unit: 'px', // Cambio de '%' a 'px' para tamaño fijo
      width: size,
      height: size, // Asegurando que el recorte es cuadrado
      aspect: aspect
    },
    mediaWidth,
    mediaHeight,
  );
}
// #endregion

const ImageCropComponent = ({ isVisible, base64, fnRecibirCropImage}) => {
  //
  // #region Internal States
  const [showControl, setShowControl] = useState(false)
  const [imgSrc, setImgSrc] = useState(base64)
  const previewCanvasRef = useRef(null)
  const imgRef = useRef(null)
  const hiddenAnchorRef = useRef(null)
  const blobUrlRef = useRef('')
  const [crop, setCrop] = useState()
  const [completedCrop, setCompletedCrop] = useState()
  const [scale, setScale] = useState(1)
  const [rotate, setRotate] = useState(0)
  const [aspect, setAspect] = useState(1 / 1)
  // #endregion

  // #region useEffect

    useEffect(()=>{
      setImgSrc(base64)
    },[base64])

    useEffect(()=>{
      toggleComponentVisibility
    },[isVisible])

  // #endregion

  // #region Internal functions

  
  
  const toggleComponentVisibility = () => {
    setShowControl(!showControl)
  }

  const handleImageLoaded = (image) => {
    console.log('Image loaded');
  }

  function onSelectFile(e) {
    if (e.target.files && e.target.files.length > 0) {
        setCrop(undefined); // Makes crop preview update between images.
        const reader = new FileReader();
        reader.addEventListener('load', () =>
            setImgSrc(reader.result ? reader.result.toString() : ''),
        );
        reader.readAsDataURL(e.target.files[0]);
    }
  }

  function onImageLoad(e) {
      if (aspect) {
          const { width, height } = e.currentTarget;
          setCrop(centerAspectCrop(width, height, aspect));
      }
  }

  useDebounceEffect(
    async () => {
      if (
        completedCrop?.width &&
        completedCrop?.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        // We use canvasPreview as it's much faster than imgPreview.
        canvasPreview(
          imgRef.current,
          previewCanvasRef.current,
          completedCrop,
          scale,
          rotate,
        )
      }
    },
    100,
    [completedCrop, scale, rotate],
  )

  function handleToggleAspectClick() {
    if (aspect) {
      setAspect(undefined);
    } else {
      setAspect(16 / 9);
  
      if (imgRef.current) {
        const { width, height } = imgRef.current;
        //const newCrop = centerAspectCrop(width, height, 16 / 9);
        const newCrop = centerAspectCrop256(width, height, 1);
        setCrop(newCrop);
        // Updates the preview
        setCompletedCrop(convertToPixelCrop(newCrop, width, height));
      }
    }
  }


  const handleSliderChange = (newValue) => {
    //console.log('newValue', newValue)
    setScale(Number (newValue))
  };

  const handleRotateSliderChange = (newValue) => {
    //console.log('newValue', newValue)
    setRotate(Number (newValue))
  };

  const handleonClickRatio = (event) => {
    console.log('newValue.target.value', event.target.value)
    switch (Number (event.target.value)) {
      case 1:
        setAspect(1 / 1);
        if (imgRef.current) {
          const { width, height } = imgRef.current;
          //const newCrop = centerAspectCrop256(width, height, 1);
          const newCrop = centerAspectCrop(width, height, 1 / 1);
          setCrop(newCrop);
          // Updates the preview
          setCompletedCrop(convertToPixelCrop(newCrop, width, height));
        }
        break;
      case 43:
        setAspect(16 / 9);
        if (imgRef.current) {
          const { width, height } = imgRef.current;
          const newCrop = centerAspectCrop(width, height, 16 / 9);
          setCrop(newCrop);
          // Updates the preview
          setCompletedCrop(convertToPixelCrop(newCrop, width, height));
        }
        break;
      case 34:
        setAspect(9 / 16);
        if (imgRef.current) {
          const { width, height } = imgRef.current;
          const newCrop = centerAspectCrop(width, height, 9 / 16);
          setCrop(newCrop);
          // Updates the preview
          setCompletedCrop(convertToPixelCrop(newCrop, width, height));
        }
        break;
    
      default:
        setAspect(undefined);
        break;
    }
  };
  // #endregion

  // #region Get CropImage

  async function onDownloadCropClick() {
    const image = imgRef.current;
    const previewCanvas = previewCanvasRef.current;
    if (!image || !previewCanvas || !completedCrop) {
      throw new Error('Crop canvas does not exist');
    }

    // This will size relative to the uploaded image size. 
    // If you want to size according to what they are looking at on screen, remove scaleX + scaleY
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    const offscreen = new OffscreenCanvas(
      completedCrop.width * scaleX,
      completedCrop.height * scaleY
    );
    const ctx = offscreen.getContext('2d');
    if (!ctx) {
      throw new Error('No 2d context');
    }

    ctx.drawImage(
      previewCanvas,
      0,
      0,
      previewCanvas.width,
      previewCanvas.height,
      0,
      0,
      offscreen.width,
      offscreen.height
    );
    // You might want { type: "image/jpeg", quality: <0 to 1> } to reduce image size
    const blob = await offscreen.convertToBlob({
      type: 'image/png',
    });

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
    }
    blobUrlRef.current = URL.createObjectURL(blob);

    if (hiddenAnchorRef.current) {
      hiddenAnchorRef.current.href = blobUrlRef.current;
      hiddenAnchorRef.current.click();
    }
  }

  async function getCroppedImageBase64() {
  const image = imgRef.current;
  const previewCanvas = previewCanvasRef.current;
  if (!image || !previewCanvas || !completedCrop) {
    throw new Error('Crop canvas does not exist');
  }

  // This will size relative to the uploaded image size. 
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;

  const offscreen = new OffscreenCanvas(
    completedCrop.width * scaleX,
    completedCrop.height * scaleY
  );
  const ctx = offscreen.getContext('2d');
  if (!ctx) {
    throw new Error('No 2d context');
  }

  ctx.drawImage(
    previewCanvas,
    0,
    0,
    previewCanvas.width,
    previewCanvas.height,
    0,
    0,
    offscreen.width,
    offscreen.height
  );

  const blob = await offscreen.convertToBlob({
    type: 'image/png',
  });

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
  }  
  // #endregion

  return (
    <React.Fragment>
      {!!isVisible && (
        <>
          {/* Escala, Rotate */}
          <Row>
            {/* <input type="file" accept="image/*" onChange={onSelectFile} /> */}
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="scale">
                  Escala
                </Label>
                <Slider
                  min={0}
                  max={1}
                  value={scale}
                  onChange={handleSliderChange}
                  step="0.1"
                  disabled={!imgSrc}
                  trackStyle={{ backgroundColor: "#838de7", height: 5 }}
                  handleStyle={{
                    borderColor: "#838de7",
                    height: 20,
                    width: 20,
                    marginTop: -8,
                    backgroundColor: "#838de7",
                  }}
                  railStyle={{ backgroundColor: "#d7dada", height: 5 }}
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="rotate">
                  Rotate
                </Label>
                <Slider
                  min={-180}
                  max={180}
                  value={rotate}
                  onChange={handleRotateSliderChange}
                  step="1"
                  disabled={!imgSrc}
                  trackStyle={{ backgroundColor: "#838de7", height: 5 }}
                  handleStyle={{
                    borderColor: "#838de7",
                    height: 20,
                    width: 20,
                    marginTop: -8,
                    backgroundColor: "#838de7",
                  }}
                  railStyle={{ backgroundColor: "#d7dada", height: 5 }}
                />
              </div>
            </Col>
          </Row>
          {/* Proporción, Crop */}
          <Row>
            {/* Prooporcion */}
            <Col md={6}>
              <div className="mb-3">
                <Label htmlFor="ratioRadio">
                  Proporción
                </Label>
                <div className="mb-2">
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ratioRadio"
                      id="ratio1"
                      value="1"
                      onClick={handleonClickRatio}
                      defaultChecked
                    />
                    <label
                      className="form-check-label"
                      htmlFor="exampleRadios1"
                    >
                      1:1
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ratioRadio"
                      id="ratio43"
                      value="43"
                      onClick={handleonClickRatio}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="exampleRadios2"
                    >
                      4:3
                    </label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="ratioRadio"
                      id="ratio34"
                      value="34"
                      onClick={handleonClickRatio}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="exampleRadios2"
                    >
                      3:4
                    </label>
                  </div>
                </div>
              </div>
            </Col>
            {/* Botón Crop */}
            <Col md={6}>
              <div className="d-flex justify-content-end mt-2">
                <Button 
                  color="primary" 
                  className="btn btn-primary w-md waves-effect waves-light"
                  onClick={async () => {  // Mark this function as async
                    try {
                      const base64String = await getCroppedImageBase64();
                      fnRecibirCropImage(base64String);
                    } catch (error) {
                      console.error('Error getting cropped image:', error);
                    }
                  }}
                >
                  Recortar
                </Button>
              </div>
            </Col>
          </Row>
          {/* Control, Imagen */}
          <Row>
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
                // minWidth={400}
                minHeight={100}
                // circularCrop
              >
                {!!imgSrc && (
                  <img
                    ref={imgRef}
                    alt="Crop me"
                    src={imgSrc}
                    style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
                    onLoad={onImageLoad}
                  />
                )}
              </ReactCrop>
              {!!completedCrop && (
                <>
                  <div 
                    style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0 }}>
                    <canvas
                      ref={previewCanvasRef}
                      style={{
                        border: '1px solid black',
                        objectFit: 'contain',
                        width: completedCrop.width,
                        height: completedCrop.height,
                      }}
                    />
                  </div>
                </>
              )}


          </Row>
        </>
      )}
    </React.Fragment>

  );
};

export default ImageCropComponent;
